import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  InputAdornment, 
  Skeleton,
  Tabs,
  Tab, Chip,
  Pagination
} from '@mui/material';
import {  FaSearch as SearchIcon } from 'react-icons/fa';
import { TbCategoryPlus } from "react-icons/tb";
import { MdDeleteForever } from "react-icons/md";
import { postFileUpload, getData, deleteData, putData } from '../../Webservice/webservice';
import Subcategories from '../../Components/Categories/Subcategories';
import { FiX, FiType, FiUploadCloud, FiTrash2, FiCircle, FiSave } from 'react-icons/fi';
import CATEGORY_ROUTES from '../../API_Route/CategoryRoute';

const PAGE_SIZE = 20; // Default page size

function Categories() {
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({
    title: '',
    image: '',
    status: 'active'
  });
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  // Pagination state (meta object)
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch categories with pagination
  const fetchCategories = async (page = 1) => {
    setLoading(true);
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const params = {
        page,
        limit: PAGE_SIZE,
        ...(searchTerm && { search: searchTerm })
      };
      const response = await getData(CATEGORY_ROUTES.GET_CATEGORY, params, token);
      setCategories(Array.isArray(response.categories) ? response.categories : []);
      // Use meta from response
      const meta = response.meta || { page: 1, totalPages: 1, total: 0 };
      setMeta({
        page: meta.page,
        totalPages: meta.totalPages,
        total: meta.total
      });
    } catch (error) {
      setCategories([]);
      setMeta({ page: 1, totalPages: 1, total: 0 });
      console.error('Fetch categories failed:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories(meta.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.page, searchTerm]);

  // Highlight matching text in search results
  const highlightMatch = (text) => {
    if (!searchTerm.trim()) return text;
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, index) =>
      regex.test(part) ?
        <mark key={index} style={{ backgroundColor: '#ff1a1a', color: 'white', padding: 0 }}>{part}</mark> :
        part
    );
  };

  // Pagination handler
  const handlePageChange = (event, newPage) => {
    setMeta((prev) => ({ ...prev, page: newPage }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleDeleteCategory = (category) => {
    setDeleteTarget({ type: 'category', value: category });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (deleteTarget && deleteTarget.type === 'category') {
      try {
        const tokenValue = sessionStorage.getItem('token');
        const token = tokenValue ? `Bearer ${tokenValue}` : '';
        const categoryToDelete = categories.find(cat => cat.title === deleteTarget.value);
        if (categoryToDelete) {
          await deleteData(`categories/delete/${categoryToDelete._id}`, token);
          fetchCategories(); // Refresh the list
        }
        handleClose();
      } catch (error) {
        console.error('Delete failed:', error);
      }
    }
  };

  const handleAddCategory = () => {
    setAddOpen(true);
  };

  const handleViewCategory = (category) => {
    setSelectedCategory(category);
    setViewOpen(true);
  };

  const handleAddCategoryClose = () => {
    setAddOpen(false);
    setNewCategory({ title: '', image: '', status: 'active' });
    setImagePreview('');
  };

  const handleViewCategoryClose = () => {
    setViewOpen(false);
    setSelectedCategory(null);
  };

  const handleAddCategorySubmit = async () => {
    try {
      const formData = new FormData();
      formData.append('title', newCategory.title);
      formData.append('status', newCategory.status);
      if (newCategory.image) {
        formData.append('image', newCategory.image);
      }
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      await postFileUpload(CATEGORY_ROUTES.ADD_CATEGORY, formData, token);
      fetchCategories(); // Refresh the list
      handleAddCategoryClose();
    } catch (error) {
      console.error('Add category failed:', error);
    }
  };

  const handleUpdateCategory = async () => {
    if (!selectedCategory || !selectedCategory._id) {
      console.error('No category ID found for update');
      return;
    }
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';

      // Check if there's a new image file
      const hasNewImage = selectedCategory.image && selectedCategory.image instanceof File;

      if (hasNewImage) {
        // Use postFileUpload for updates with images
        const formData = new FormData();
        formData.append('title', selectedCategory.title);
        formData.append('status', selectedCategory.status);
        formData.append('image', selectedCategory.image);
        await putData(`categories/update/${selectedCategory._id}`, formData, token);
      } else {
        // Use putData for text-only updates
        const updateData = {
          title: selectedCategory.title,
          status: selectedCategory.status
        };
        await putData(`categories/update/${selectedCategory._id}`, updateData, token);
      }

      fetchCategories(); // Refresh the list
      handleViewCategoryClose();
    } catch (error) {
      console.error('Update category failed:', error);
    }
  };

  return (
    <Box p={2} style={{  borderRadius: '10px' }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5"
       sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 700, color: "#1a1a1a", display: 'flex', alignItems: 'center', gap: 1 }}>
          Categories and Subcategories Management
          {/* <Chip
            label={`${meta.total} ${meta.total === 1 ? 'Item' : 'Items'}`}
            size="small"
            sx={{ color: 'red', fontFamily: 'Mulish, sans-serif', fontWeight: '700' }}
            variant="contained"
          /> */}
        </Typography>
        
        {/* {activeTab === 0 && (
          <Button
            variant="contained"
            startIcon={<TbCategoryPlus />}
            sx={{
              fontFamily: 'Mulish, sans-serif',
              background: '#2B333A',
              color: 'white',
              textTransform: 'none',
            //   border:'1px solid black',
              borderRadius: 2,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none',
                background: '#2B333A',
              }
            }}
            onClick={handleAddCategory}
          >
            Add Category
          </Button>
        )} */}
      </Box>

      {/* Tabs */}
      <Box sx={{ 
    backgroundColor: 'action.hover',
    borderRadius: '8px',
    p: 0.5,
    mb: 1,
    display: 'inline-flex'
}}>
    <Tabs
        value={activeTab}
        onChange={handleTabChange}
        aria-label="categories and subcategories tabs"
        sx={{
            '& .MuiTabs-indicator': {
                display: 'none'
            }
        }}
    >
        <Tab 
            label="Categories" 
            sx={{ 
                fontWeight: 700, 
                textTransform: 'none',
                fontFamily: 'Mulish, sans-serif',
                borderRadius: '6px',
                mx: 0.5,
                py: 1,
                px: 3,
                color: activeTab === 0 ? 'white' : 'text.secondary',
                backgroundColor: activeTab === 0 ? '#2B333A' : 'transparent',
                '&.Mui-selected': {
                    color: 'white',
                    backgroundColor: '#2B333A'
                },
                '&:hover': {
                    backgroundColor: activeTab === 0 ? '#2B333A' : '#bbbabaff'
                }
            }} 
        />
        <Tab 
            label="Subcategories" 
            sx={{ 
                fontWeight: 700, 
                textTransform: 'none',
                fontFamily: 'Mulish, sans-serif',
                borderRadius: '6px',
                mx: 0.5,
                py: 1,
                px: 3,
                color: activeTab === 1 ? 'white' : 'text.secondary',
                backgroundColor: activeTab === 1 ? '#2B333A' : 'transparent',
                '&.Mui-selected': {
                    color: 'white',
                    backgroundColor: '#2B333A'
                },
                '&:hover': {
                    backgroundColor: activeTab === 1 ? '#2B333A' : '#bbbabaff'
                }
            }} 
        />
    </Tabs>
</Box>

      {/* Toolbar - Show only for Categories tab */}
      {activeTab === 0 && (
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={5} lg={4}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search by title..."
                size="small"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setMeta({ ...meta, page: 1 });
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon size={20} color="#9e9e9e" />
                    </InputAdornment>
                  ),
                  sx: { 
                    borderRadius: '10px',
                    backgroundColor: 'white',
                    '& fieldset': { borderColor: '#e0e0e0' },
                    '&:hover fieldset': { borderColor: '#ced4da' },
                    '&.Mui-focused fieldset': { borderColor: '#2B333A' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={7} lg={8} sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              {searchTerm && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSearchTerm('');
                    setMeta({ ...meta, page: 1 });
                  }}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '8px',
                    borderColor: '#ef5350',
                    color: '#ef5350',
                    fontWeight: 600,
                    px: 3,
                    '&:hover': {
                      borderColor: '#d32f2f',
                      backgroundColor: 'rgba(239, 83, 80, 0.08)'
                    }
                  }}
                >
                  Clear Search
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<TbCategoryPlus size={20} />}
                onClick={handleAddCategory}
                sx={{
                  fontFamily: 'Mulish, sans-serif',
                  background: '#2B333A',
                  color: 'white',
                  textTransform: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  px: 3,
                  boxShadow: '0 4px 12px rgba(43, 51, 58, 0.2)',
                  '&:hover': {
                    background: '#1a1f24',
                    boxShadow: '0 6px 16px rgba(43, 51, 58, 0.3)',
                  }
                }}
              >
                Add Category
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Tab Content */}
      {activeTab === 0 ? (
        <>
          <TableContainer component={Paper}
            elevation={0}
            sx={{
              borderRadius: '12px',
              border: '1px solid #e0e0e0',
              overflow: 'hidden'
            }}>
            <Table sx={{ minWidth: 650 }} aria-label="category table">
              <TableHead>
                <TableRow sx={{
                  backgroundColor: '#f8f9fa',
                  '& th': {
                    fontFamily: 'Mulish, sans-serif',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    color: '#495057',
                    borderBottom: '2px solid #e9ecef',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    py: 2
                  }
                }}>
                  <TableCell sx={{ pl: 4 }}>Title</TableCell>
                  <TableCell align="center">Image</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 6 }).map((_, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={{ pl: 4 }}>
                        <Skeleton width="60%" height={25} />
                      </TableCell>
                      <TableCell align="center">
                        <Skeleton variant="rounded" width={50} height={50} sx={{ mx: 'auto', borderRadius: '8px' }} />
                      </TableCell>
                      <TableCell align="center">
                        <Skeleton width={80} height={32} sx={{ mx: 'auto', borderRadius: '16px' }} />
                      </TableCell>
                      <TableCell align="center">
                        <Skeleton width={40} height={40} variant="circular" sx={{ mx: 'auto' }} />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  categories.length > 0 ? (
                    categories.map((cat) => (
                      <TableRow
                        key={cat._id}
                        hover
                        onClick={() => handleViewCategory(cat)}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#f8f9fa' },
                          '& td': {
                            py: 1.5,
                            borderBottom: '1px solid #f1f3f5',
                            fontFamily: 'Mulish, sans-serif'
                          },
                          '&:last-child td': { borderBottom: 0 }
                        }}
                      >
                        <TableCell sx={{ pl: 4 }}>
                          <Typography fontWeight={600} variant="body2" sx={{ color: '#212529', fontSize: '0.95rem' }}>
                            {highlightMatch(cat.title)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ 
                            width: 50, 
                            height: 50, 
                            borderRadius: '8px', 
                            overflow: 'hidden', 
                            mx: 'auto',
                            border: '1px solid #dee2e6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#fff'
                          }}>
                            <img 
                              src={cat.image} 
                              alt={cat.title} 
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'cover' 
                              }} 
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={highlightMatch(cat.status)}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              textTransform: 'capitalize',
                              backgroundColor: cat.status === 'active' ? 'rgba(40, 167, 69, 0.1)' : 'rgba(108, 117, 125, 0.1)',
                              color: cat.status === 'active' ? '#28a745' : '#6c757d',
                              fontFamily: 'Mulish, sans-serif',
                              px: 1,
                              height: 28
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                            {/* <IconButton
                              size="small"
                              sx={{ 
                                color: '#0d6efd', 
                                backgroundColor: 'rgba(13, 110, 253, 0.05)',
                                '&:hover': { backgroundColor: 'rgba(13, 110, 253, 0.15)' }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewCategory(cat);
                              }}
                            >
                              <FiEdit2 size={18} />
                            </IconButton> */}
                            <IconButton
                              size="small"
                              sx={{ 
                                color: '#dc3545', 
                                backgroundColor: 'rgba(220, 53, 69, 0.05)',
                                transition: 'all 0.2s',
                                '&:hover': { 
                                  backgroundColor: 'rgba(220, 53, 69, 0.15)',
                                  transform: 'scale(1.1)'
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteCategory(cat.title);
                              }}
                            >
                              <MdDeleteForever size={20} />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: 'center', py: 8 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: 0.7 }}>
                          <img 
                            src="./img/nodata.png" 
                            alt="No categories" 
                            style={{ width: 120, marginBottom: 16, opacity: 0.8 }} 
                          />
                          <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600, color: '#495057' }}>
                            No categories found
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {searchTerm ? 'Try adjusting your search terms' : 'Get started by adding a new category'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Component (centered, like ActivityLog) */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mt: 3,
            '& .MuiPagination-ul': {
              flexWrap: 'nowrap'
            }
          }}>
            <Pagination
              count={meta.totalPages}
              page={meta.page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              disabled={loading}
              showFirstButton
              showLastButton
            />
          </Box>
          {meta.total > 0 && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
              Showing {(meta.page - 1) * PAGE_SIZE + 1} to {Math.min(meta.page * PAGE_SIZE, meta.total)} of {meta.total} entries
            </Typography>
          )}
        </>
      ) : (
        <Subcategories categories={categories} />
      )}

      {/* Add Category Modal */}
      <Dialog
        open={addOpen}
        onClose={handleAddCategoryClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.25rem',
            fontWeight: 600,
            py: 2,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          Add New Category
          <IconButton
            onClick={handleAddCategoryClose}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.05)'
              }
            }}
          >
            <FiX size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: 3, px: 3 }}>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: '10PX' }}>
            {/* Title Field */}
            <TextField
              variant="outlined"
              label="Category Name"
              value={newCategory.title}
              onChange={e => setNewCategory({ ...newCategory, title: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FiType color="#6c757d" size={18} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: '8px',
                  '& input': {
                    py: 1.5
                  }
                }
              }}
            />

            {/* Image Upload */}
            <Box>
              <Typography variant="subtitle2" sx={{
                mb: 1,
                fontWeight: 500,
                color: 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <FiUploadCloud size={16} />
                Category Image
              </Typography>

              <Box
                component="label"
                htmlFor="category-image-upload"
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(0, 123, 255, 0.05)'
                  }
                }}
              >
                {imagePreview ? (
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: '100%',
                        maxHeight: 200,
                        objectFit: 'contain',
                        borderRadius: 6
                      }}
                    />
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.7)'
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setImagePreview('');
                        setNewCategory({ ...newCategory, image: '' });
                      }}
                    >
                      <FiTrash2 size={16} />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    <FiUploadCloud size={48} color="#adb5bd" style={{ marginBottom: '8px' }} />
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <Box component="span" sx={{ color: 'primary.main', fontWeight: 500 }}>
                        Click to upload
                      </Box> or drag and drop
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                      PNG, JPG, GIF (Max 2MB)
                    </Typography>
                  </>
                )}

                <input
                  id="category-image-upload"
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={e => {
                    const file = e.target.files[0];
                    if (file) {
                      setNewCategory({ ...newCategory, image: file });
                      setImagePreview(URL.createObjectURL(file));
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Status Selector */}
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={newCategory.status}
                label="Status"
                onChange={e => setNewCategory({ ...newCategory, status: e.target.value })}
                variant="outlined"
                sx={{
                  borderRadius: '8px',
                  '& .MuiSelect-select': {
                    py: 1.5
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      marginTop: '4px'
                    }
                  }
                }}
              >
                <MenuItem value="active">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FiCircle size={14} color="#28a745" />
                    Active
                  </Box>
                </MenuItem>
                <MenuItem value="inactive">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FiCircle size={14} color="#6c757d" />
                    Inactive
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          px: 3,
          py: 2,
          gap: 2
        }}>
          <Button
            onClick={handleAddCategoryClose}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              px: 3,
              py: 1,
              border:'1px solid grey',
              color:"grey",
              fontWeight: 500,
              '&:hover': {
                borderWidth: '1.5px',
                backgroundColor: '#f8f9fa'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddCategorySubmit}
            disabled={!newCategory.title}
            sx={{
              border:"1px solid black",
              borderRadius: '8px',
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 500,
              boxShadow: 'none',
              background: '#2B333A',
              color: 'black',
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                background: '#2B333A'
              },
              '&:disabled': {
                background: '#e9ecef',
                color: '#adb5bd'
              }
            }}
          >
            Create Category
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Category Modal */}
      <Dialog
        open={viewOpen}
        onClose={handleViewCategoryClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.25rem',
            fontWeight: 600,
            py: 2,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          Edit Category
          <IconButton
            onClick={handleViewCategoryClose}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.05)'
              }
            }}
          >
            <FiX size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: 3, px: 3 }}>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 , marginTop:'10px'}}>
            {/* Title Field */}
            <TextField
              variant="outlined"
              label="Category Name"
              value={selectedCategory?.title || ''}
              onChange={e => setSelectedCategory({ ...selectedCategory, title: e.target.value })}
              fullWidth
            />

            {/* Image Upload */}
            <Box>
              <Typography variant="subtitle2" sx={{
                mb: 1,
                fontWeight: 500,
                color: 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <FiUploadCloud size={16} />
                Category Image
              </Typography>

              <Box
                component="label"
                htmlFor="edit-category-image-upload"
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(0, 123, 255, 0.05)'
                  }
                }}
              >
                {selectedCategory?.image ? (
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    <img
                      src={selectedCategory.image instanceof File ?
                        URL.createObjectURL(selectedCategory.image) :
                        selectedCategory.image}
                      alt="Preview"
                      style={{
                        width: '100%',
                        maxHeight: 200,
                        objectFit: 'contain',
                        borderRadius: 6
                      }}
                    />
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.7)'
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategory({ ...selectedCategory, image: null });
                      }}
                    >
                      <FiTrash2 size={16} />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    <FiUploadCloud size={48} color="#adb5bd" style={{ marginBottom: '8px' }} />
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <Box component="span" sx={{ color: 'primary.main', fontWeight: 500 }}>
                        Click to upload
                      </Box> or drag and drop
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                      PNG, JPG, GIF (Max 2MB)
                    </Typography>
                  </>
                )}

                <input
                  id="edit-category-image-upload"
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={e => {
                    const file = e.target.files[0];
                    if (file) {
                      setSelectedCategory({ ...selectedCategory, image: file });
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Status Selector */}
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={selectedCategory?.status || 'active'}
                label="Status"
                onChange={e => setSelectedCategory({ ...selectedCategory, status: e.target.value })}
                variant="outlined"
                sx={{
                  borderRadius: '8px',
                  '& .MuiSelect-select': {
                    py: 1.5
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      marginTop: '4px'
                    }
                  }
                }}
              >
                <MenuItem value="active">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FiCircle size={14} color="#28a745" />
                    Active
                  </Box>
                </MenuItem>
                <MenuItem value="inactive">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FiCircle size={14} color="#6c757d" />
                    Inactive
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          px: 3,
          py: 2,
          gap: 2
        }}>
          <Button
            onClick={handleViewCategoryClose}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              px: 3,
              py: 1,
              borderWidth: '1.5px',
              fontWeight: 500,
              borderColor: 'red',
                color: 'red',
                '&:hover': {
                  borderColor: 'darkred',
                backgroundColor: 'rgba(255,0,0,0.1)'
                }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateCategory}
            disabled={!selectedCategory?.title}
            startIcon={<FiSave size={18} />}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 500,
              boxShadow: 'none',
              background: '#2B333A',
            //   border: '1px solid black',
              fontFamily: 'Mulish, sans-serif',
              color: 'white',
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                background: '#2B333A'
              },
              '&:disabled': {
                background: '#e9ecef',
                color: '#adb5bd'
              }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            borderRadius: '10px',
            padding: '20px',
            minWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ fontSize: '20px', fontWeight: 'bold', textAlign: 'center' }}>
          <img style={{ width: '80px' }} src='./img/del1.png' alt='delete' />
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ textAlign: 'center', fontSize: '16px', mb: 1 }}>
            {deleteTarget && deleteTarget.type === 'category' && (
              <>Are you sure you want to delete category <b>{deleteTarget.value}</b> and all its subcategories?</>
            )}
          </DialogContentText>
          <Box sx={{
            textAlign: 'center',
            backgroundColor: '#fff3cd',
            padding: '12px',
            borderRadius: '4px',
            borderLeft: '4px solid #ffc107',
            marginBottom: '2px'
          }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#856404', marginBottom: '3px' }}>
              Warning
            </Typography>
            <Typography variant="body2" sx={{ color: '#856404' }}>
              By deleting, you won't be able to access it again.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: '20px', padding: '20px' }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              minWidth: '120px',
              borderColor: 'gray',
              color: 'gray',
              '&:hover': {
                borderColor: 'darkgray',
                backgroundColor: 'rgba(0,0,0,0.05)'
              }
            }}
          >
            No, Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              minWidth: '120px',
              backgroundColor: 'red',
              '&:hover': {
                backgroundColor: '#bb2d3b'
              }
            }}
            autoFocus
          >
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Categories;